import PageTitle from "../../components/common/PageTitle/PageTitle";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { useEffect, useRef, useState } from "react";
import { EmployeesWrapper } from "./Employees.styles";
import { useProcessingRequest, useResponseValue } from "../../store/selectors/tableManagementSelector";
import { CircularIndeterminate } from "../../components/common/CircularIndeterminate";
import { setResponseValue } from "../../store/reducers/tableManagementReducer";
import { useServerSidePagination } from "../../hooks/PaginationHooks";
import EmployeesSearchBar from "./EmployeesSearchBar";
import EmployeesTable from "./EmployeesTable/EmployeesTable";
import { fetchPaginatedEmployeesThunk } from "../../store/thunk/employeesThunk";
import { useGroupsData } from "../../store/selectors/groupSelectors";
import { fetchGroupsThunk } from "../../store/thunk/groupsThunk";
import { EmployeeUserType } from "../../types/employeeType";
import { fetchSubCompaniesThunk } from "../../store/thunk/subCompaniesThunk";
import CreateOrEditEmployeeDialog from "./EmployeesTable/CreateOrEditEmployeeDialog";
import { fetchServiceModulesThunk } from "../../store/thunk/serviceModulesThunk";
import { EmployeePageFiltersResponse, EmployeeUserFilterParameters } from "../../types/employeePageFilters";
import { fetchEmployeePageFiltersThunk } from "../../store/thunk/employeesThunk";
import { keyValueStorage } from "../../common/logic/KeyValueStore";

const Employees = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const processingRequest = useProcessingRequest()
  const responseValue = useResponseValue()
  const groupList = useGroupsData();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState<EmployeePageFiltersResponse>({
    groups: [], subCompanies: [], actionModules: [], serviceModules: [],
  });

  const defaultFilters: EmployeeUserFilterParameters = {
    groupIds: [], subCompanyIds: [], actionModuleIds: [], serviceModuleIds: [],
  };

  const [selectedFilters, setSelectedFilters] = useState<EmployeeUserFilterParameters>(() =>
    keyValueStorage.getObject<EmployeeUserFilterParameters>("employees_filters", defaultFilters)
  );

  useEffect(() => {
    dispatch(fetchGroupsThunk());
    dispatch(fetchSubCompaniesThunk());
    dispatch(fetchServiceModulesThunk());
    dispatch(fetchEmployeePageFiltersThunk()).then((action) => {
      if (fetchEmployeePageFiltersThunk.fulfilled.match(action) && action.payload) {
        const options = action.payload;
        setFilterOptions(options);

        const validIds = {
          group: new Set(options.groups.map(g => g.id)),
          subCompany: new Set(options.subCompanies.map(s => s.id)),
          actionModule: new Set(options.actionModules.map(a => a.id)),
          serviceModule: new Set(options.serviceModules.map(s => s.id)),
        };
        setSelectedFilters(prev => {
          const cleaned: EmployeeUserFilterParameters = {
            groupIds: prev.groupIds.filter(id => validIds.group.has(id)),
            subCompanyIds: prev.subCompanyIds.filter(id => validIds.subCompany.has(id)),
            actionModuleIds: prev.actionModuleIds.filter(id => validIds.actionModule.has(id)),
            serviceModuleIds: prev.serviceModuleIds.filter(id => validIds.serviceModule.has(id)),
          };
          keyValueStorage.setObject("employees_filters", cleaned);
          return cleaned;
        });
      }
    });
  }, [dispatch]);

  const paginatedData = useServerSidePagination<EmployeeUserType>(
    async (paginationParameters) => {
      const resultAction = await dispatch(fetchPaginatedEmployeesThunk({
          paginationParams: paginationParameters,
          filters: selectedFilters,
      }));

      if (fetchPaginatedEmployeesThunk.fulfilled.match(resultAction)) {
        return resultAction.payload;
      } else {
        return { items: [], totalCount: 0 };
      }
    },
    []
  );

  useEffect(() => {
    if (responseValue.includes("fulfilled")) {
      paginatedData.updateTableItems();
      dispatch(setResponseValue(""))
    }
  }, [dispatch, paginatedData, responseValue]);

  const filterDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleFilterChange = (filter: Partial<EmployeeUserFilterParameters>) => {
    setSelectedFilters(prev => {
      const updated = { ...prev, ...filter };
      keyValueStorage.setObject("employees_filters", updated);
      return updated;
    });
    clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      paginatedData.updateTableItems();
    }, 500);
  };

  return (
    <>
      <PageTitle
        title={t("employeesPage.title")}
      />
      <EmployeesWrapper>
        <EmployeesSearchBar
          handleOnSearch={paginatedData.handleOnSearch}
          onCreateEmployee={() => setOpenCreateModal(true)}
          filterOptions={filterOptions}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
        />
        <EmployeesTable
          data={paginatedData}
          groupList={groupList}
        />
      </EmployeesWrapper>

      {openCreateModal && <CreateOrEditEmployeeDialog
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        groupList={groupList}
        mode="create"
      />}
      <CircularIndeterminate processingRequest={processingRequest} />
    </>
  );
};

export default Employees;
